'use client';

import React from 'react';
import {
  PiCalendarBlank, PiMapPin, PiTagSimple, PiTicket,
} from 'react-icons/pi';
import EventDTO from '../models/event';
import Button from './atoms/Button';
import EventBannerImage from './atoms/EventBannerImage/EventBannerImage';
import { parseFriendlyDate } from '../utils/dates';
import LoadingText from './atoms/LoadingText';
import { useGetEventCapacityQuery } from '../store/apis/events';
import useGetUserRole from '../hooks/useGetUserRole';
import useIsBackoffice from '../hooks/useIsBackoffice';
import usePrefetchOnIntent from '../hooks/usePrefetchOnIntent';

const FALLBACK_BANNER = '/favicon.png';
// Public grid is 1 col on mobile (~100vw), 2 cols on md (~50vw), 3 cols on lg+ (~33vw).
// Matches the grid in `AvailableEventsPage` and `EventPageClient`'s recos.
const CARD_IMAGE_SIZES = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';

interface EventCardProps {
  event: EventDTO;
  onSelect: (eventId: string) => void;
  colorToast?: string;
  textButton?: string;
  textToast?: string;
  adminEvents?: boolean;
  hideButton?: boolean;
  /**
   * Public category to badge on the image (slug + display name). When
   * absent, the chip is omitted — graceful while the public events
   * endpoint is still in the process of exposing taxonomy categories.
   */
  category?: { slug: string; name: string };
  /** Pre-rendered icon for the category chip. Defaults to a generic tag. */
  categoryIcon?: React.ReactNode;
}

const getCustomLabelCTA = (event: EventDTO) => {
  if (event.custom_ticket_label === 'registro') {
    return 'Registrarse';
  }
  return 'Adquirir entradas';
};

const LandingEventCard: React.FC<EventCardProps> = ({
  event, onSelect, colorToast = 'bg-warning', textToast, textButton = getCustomLabelCTA(event), adminEvents, hideButton = false, category, categoryIcon,
}) => {
  let content;
  const isBackoffice = useIsBackoffice();
  if (adminEvents) {
    const role = useGetUserRole();
    const { data: capacity, isFetching: isFetchingCapacity } = useGetEventCapacityQuery({ eventId: event.id, businessId: event.business_id });
    const ticketsSold = event.tickets.reduce((pv, cv) => pv + (cv?.amount_taken ?? 0), 0);
    content = (role === 'ADMIN' || role === 'ROOT') && adminEvents && (
      <div className="flex items-center text-text-secondary text-md-medium break-words whitespace-normal text-center">
        <PiTicket className="mr-1 text-md-medium" />
        <LoadingText
          text={`${capacity?.used_tickets ?? '?'}/${ticketsSold} tickets vendidos`}
          isLoading={isFetchingCapacity}
        />
      </div>
    );
  }

  const eventHref = isBackoffice ? `/admin/${event.business_id}/events/${event.id}` : `/events/${event.slug}`;
  const prefetchHandlers = usePrefetchOnIntent(eventHref, !isBackoffice);

  // Admin keeps the legacy "blur card" look so the dashboard stays consistent.
  // Public home / event recommendations / white-label preview get the new card.
  if (adminEvents) {
    return (
      <a
        href={eventHref}
        onClick={(e) => {
          e.preventDefault();
          onSelect(isBackoffice ? event.id : event.slug);
        }}
        {...prefetchHandlers}
        className="group flex flex-col items-center w-full h-full p-3 rounded-3xl landing-event-card-shadow bg-blur-lg bg-opacity-heavy gap-3 cursor-pointer no-underline text-inherit"
      >
        <div className="w-full relative rounded-2xl">
          {textToast && <div className={`${colorToast} p-1 rounded-3xl w-fit absolute right-0 top-0 z-10 text-sm-medium ${colorToast === 'bg-warning-300' ? 'text-text-primary' : ''}`}>{textToast}</div>}
          <div className="overflow-hidden rounded-2xl relative aspect-[1920/700] bg-surface-secondary">
            <EventBannerImage
              src={event.banner_image || FALLBACK_BANNER}
              alt={`${event.name} - ${event.business_name}`}
              sizes={CARD_IMAGE_SIZES}
              loading="lazy"
              className="object-cover rounded-2xl group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>

        <div className="flex flex-col items-center w-full gap-3 h-full">
          <div className="w-full">
            <h2 className="text-xl-semibold text-text-primary break-words whitespace-normal text-center overflow-hidden text-ellipsis" style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}>{event.name}</h2>
          </div>
          <p className="text-text-secondary text-sm-regular">Organiza: {event.business_name}</p>
          <p className="flex items-center text-text-secondary text-md-medium">
            <PiCalendarBlank className="mr-1 text-md-medium" />
            {parseFriendlyDate(event.date, true, true)}
          </p>
          <p className="flex items-center text-text-secondary text-md-medium break-words whitespace-normal text-center">
            <PiMapPin className="mr-1 text-md-medium " />
            {event.place}
          </p>
          {content}
          {!hideButton ? <div className="flex h-full items-end w-fit mb-3">
            <Button
              variant="secondary"
              disabled={!adminEvents && !event.active}
            >
              {textButton}
            </Button></div> : null}
        </div>
      </a>
    );
  }

  // Public card.
  return (
    <a
      href={eventHref}
      onClick={(e) => {
        e.preventDefault();
        onSelect(isBackoffice ? event.id : event.slug);
      }}
      {...prefetchHandlers}
      className="group flex flex-col w-full h-full p-2 pb-6 rounded-3xl border border-border-secondary bg-surface-base landing-event-card-shadow gap-3 cursor-pointer no-underline text-inherit transition-transform duration-200 hover:-translate-y-1"
    >
      <div className="relative w-full overflow-hidden rounded-2xl aspect-[1920/700] bg-surface-secondary">
        {textToast && (
          <span className={`${colorToast} ${colorToast === 'bg-warning-300' ? 'text-text-primary' : 'text-white'} absolute top-2 left-2 z-10 px-2.5 py-1 rounded-full text-xs-semibold shadow-sm`}>
            {textToast}
          </span>
        )}
        <EventBannerImage
          src={event.banner_image || FALLBACK_BANNER}
          alt={`${event.name} - ${event.business_name}`}
          sizes={CARD_IMAGE_SIZES}
          loading="lazy"
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {category && (
          <span className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 shadow-sm text-xs-semibold text-black">
            {categoryIcon ?? <PiTagSimple className="text-sm" />}
            {category.name}
          </span>
        )}
      </div>

      <div className="flex flex-col items-center text-center w-full flex-1 gap-2.5 px-4 pt-3">
        <h2 className="text-xl-bold text-text-primary leading-tight text-pretty m-0">
          {event.name}
        </h2>
        <p className="text-sm-regular text-text-tertiary m-0">
          <span className="text-text-secondary font-medium">Organiza:</span> {event.business_name}
        </p>
        <p className="flex items-center gap-1.5 text-sm-medium text-text-secondary m-0">
          <PiCalendarBlank className="text-primary-500 text-base" />
          {parseFriendlyDate(event.date, true, true)}
        </p>
        <p className="flex items-center gap-1.5 text-sm-medium text-text-secondary break-words whitespace-normal m-0">
          <PiMapPin className="text-primary-500 text-base" />
          {event.place}
        </p>
        {content}
        {!hideButton && (
          <div className="flex h-full items-end mt-2">
            <button
              type="button"
              disabled={!event.active}
              className="inline-flex items-center justify-center px-7 py-3 rounded-full border-[1.5px] border-primary-500 bg-surface-base text-text-primary text-md-bold transition-colors duration-150 hover:bg-primary-500 hover:text-white disabled:opacity-50 disabled:border-neutral-400 disabled:hover:bg-surface-base disabled:hover:text-text-primary disabled:cursor-not-allowed"
            >
              {textButton}
            </button>
          </div>
        )}
      </div>
    </a>
  );
};

export default LandingEventCard;
